import { z } from 'zod/v3';
import { ok } from 'neverthrow';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
  ActionTrackMetadata,
} from '@cotera/client/v0/actions/framework';
import { AgentModel } from './agent.model';
import type { ActionTrackingContext } from '../actions/framework/action-tracking';
import { DG } from '@cotera/sdk/core';
import type { RichTextEditorRef } from '@cotera/client/app/components/app/rte';
import { TEMPLATES } from './templates';

type Params = {
  templateKey: keyof typeof TEMPLATES;
  editorRef?: React.RefObject<RichTextEditorRef>;
};

export class ApplyTemplateAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Apply Agent Template';
  description = 'Apply a pre-built template to the agent editor';
  inputSchema = z.object({
    templateKey: z.enum([
      'Google',
      'FindMentionsOf',
      'MeetingSummary',
      'ContentClassifier',
    ]),
    editorRef: z.any().optional(),
  });
  discrete = true;

  applicable(context: ApplicableContext): boolean {
    return context.hasTargetedModelOfType(AgentModel);
  }

  async execute(
    params: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const agent = context.targetedModelOfType(AgentModel);

    if (agent === null) {
      return ok({});
    }

    const template = TEMPLATES[params.templateKey];
    if (template === undefined) {
      return ok({});
    }

    // Create editor state from template
    const templateState = new DG.DocumentEditor.EditorState(template);
    const newState = templateState.setTitle(
      templateState.title()?.replace('Template: ', '') ?? ''
    );
    const finalState = newState.getState();

    // Update agent model
    agent.formState.set({
      ...agent.formState.snapshot(),
      agentState: finalState,
    });

    // Update editor UI if ref is provided
    if (
      params.editorRef?.current?.editor !== null &&
      params.editorRef?.current?.editor !== undefined
    ) {
      params.editorRef.current.editor.commands.setContent(finalState, {
        emitUpdate: true,
      });
    }

    return ok({});
  }

  track({
    payload,
    context,
  }: ActionTrackingContext<
    Params,
    { t: string; message?: string }[]
  >): ActionTrackMetadata | null {
    const agent = context.targetedModelOfType(AgentModel);
    if (agent === null) {
      return null;
    }

    return {
      event: 'agent_template_applied',
      properties: {
        agentId: agent.id,
        agentName: agent.name.snapshot(),
        templateKey: payload.templateKey,
        isNew: agent.isNew,
      },
    };
  }
}
